import { createDerived, createStore, getValue, update } from '@kundinos/nanostores';
import { useStore } from '@kundinos/nanostores/react';

import { DIContainer } from '@Contexts/DI';
import ApiProduct from '@Api/Product';

import type { AdditionalData, PickPairData, SetData } from '@Api/Product/getSetItem';
import type { CreditData, ProductPriceData } from '@Types/Product';

export interface ConstructorStoreData {
  products: PickPairData[];
  additional: PickPairData[];
}

interface SetStoreData {
  inited: boolean;
  products: PickPairData[];
  additional: AdditionalData;
}

interface MainData {
  productImages: string[];
  url?: string;
  price?: ProductPriceData;
  set?: SetData;
  configuratorShopProductId?: number;
  credit?: CreditData[];
}
interface ProductStoreData extends MainData {
  inited: boolean;
  loading: boolean;
}

interface Params {
  searchParams: URLSearchParams;
  typeIds?: number[];
}

const getSelectedProduct = (item: PickPairData): PickPairData => {
  return {
    ...item,
    parameters: item.parameters.map((parameter) => {
      return {
        ...parameter,
        items: parameter.items.filter((p) => p.every((elem) => elem.selected)),
      };
    }),
  };
};

export const setStore = createStore<SetStoreData>(() => {
  setStore.set({
    inited: false,
    products: [],
    additional: {
      actions: [],
      products: [],
    },
  });
});

export const productInfoStore = createStore<ProductStoreData>(() => {
  productInfoStore.set({
    inited: false,
    loading: false,
    productImages: [],
    url: null,
    price: null,
  });
});

export const selectedSetStore = createDerived(setStore, ({ products, additional }) => {
  return [...products, ...additional.products].map((product) => product.id);
});

export const actionsStore = createDerived(setStore, ({ additional }) => {
  return additional.actions.map((action) => {
    if (
      additional.products.filter((product) => product.type.id === action.product.type.id).length < 3
    ) {
      return { ...action, active: true };
    }
    return { ...action, active: false };
  });
});

export const init = (set: SetData) => {
  update(setStore, (prev) => {
    if (prev?.inited) return prev;

    return {
      inited: true,
      products: set.products || [],
      additional: {
        actions: set.additional.actions || [],
        products: set.additional.products || [],
      },
    };
  });

  if (set.products?.length < 1) return;

  update(productInfoStore, (prev) => {
    if (prev?.inited) return prev;

    const productImages = [];
    // Располагаем изображения в соответствии с типом товара, чтобы изображения простыни (с типом id = 295 или id = 393) выводились первыми.
    (set.products || []).forEach((product) => {
      if (product.type.id === 295 || product.type.id === 393) {
        return productImages.unshift(product.productImage);
      }
      return productImages.push(product.productImage);
    });

    return {
      inited: true,
      loading: false,
      productImages,
    };
  });
};

export const changeByType = (item: PickPairData) => {
  update(setStore, (prev) => {
    return {
      ...prev,
      products: prev.products.map((prevItem) => {
        return prevItem.typeIds.includes(item.type.id) ? getSelectedProduct(item) : prevItem;
      }),
    };
  });
};

export const changeAdditionalByIndex = (item: PickPairData, index: number) => {
  update(setStore, (prev) => {
    return {
      ...prev,
      additional: {
        actions: prev.additional.actions,
        products: prev.additional.products.map((product, currentIndex) => {
          return index === currentIndex ? getSelectedProduct(item) : product;
        }),
      },
    };
  });
};

export const add = (item: PickPairData) => {
  update(setStore, (prev) => {
    return {
      ...prev,
      additional: {
        actions: prev.additional.actions,
        products: [...prev.additional.products, getSelectedProduct(item)],
      },
    };
  });
};

export const remove = (item: PickPairData) => {
  update(setStore, (prev) => {
    const index = prev.additional.products.findIndex((product) => product.id === item.id);
    const products = prev.additional.products;
    products.splice(index, 1);

    return {
      ...prev,
      additional: {
        ...prev.additional,
        products,
      },
    };
  });
};

export const getConstructorUrl = async (params: Params) => {
  const { searchParams, typeIds } = params;
  const { logger } = DIContainer.getDeps();
  const set = getValue(setStore);
  let url: string = null;

  try {
    const parameters = {
      products: set.products.map((product) => product.id),
      additional: set.additional.products.map((product) => product.id),
      typeIds,
    };

    update(productInfoStore, (prev) => {
      return {
        ...prev,
        loading: true,
      };
    });

    const res = await ApiProduct.getSetPage(parameters);
    url = res.url;

    update(productInfoStore, (prev) => {
      return {
        ...prev,
        ...res,
        loading: false,
      };
    });
    update(setStore, (prev) => {
      return {
        ...prev,
        products: res.set?.products?.length > 0 ? res.set.products : prev.products,
      };
    });
  } catch (e) {
    logger.log(e);
  } finally {
    const keysToDelete: string[] = ['products', 'additional'];
    const keysForDel = [];

    searchParams.forEach((value, key) => {
      const needRemove = keysToDelete.some((keyToDelete) => key.includes(keyToDelete));
      if (needRemove) {
        keysForDel.push(key);
      }
    });

    keysForDel.forEach((key) => {
      searchParams.delete(key);
    });

    return `${url}&${searchParams.toString()}`;
  }
};

export const clear = () => {
  setStore.set({
    inited: false,
    products: [],
    additional: {
      actions: [],
      products: [],
    },
  });

  productInfoStore.set({
    inited: false,
    loading: false,
    productImages: [],
    url: null,
    price: null,
  });
};

export const hasWarningsStore = createDerived(setStore, (set) => {
  return [...set.products, ...set.additional.products].some((product) => !!product.warning);
});

export const useSet = () => {
  return useStore(setStore);
};

export const useActions = () => {
  return useStore(actionsStore);
};

export const useProductInfo = () => {
  return useStore(productInfoStore);
};

export const useSelectedSet = () => {
  return useStore(selectedSetStore);
};

export const useHasWarnings = () => {
  return useStore(hasWarningsStore);
};
